import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
  Tooltip,
} from '@apollo/orbit';
import React from 'react';

import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { SeriesControls } from 'src/components/charts/helpers/SeriesControls';
import {
  Histogram,
  usePrepareHistogramData,
} from 'src/components/charts/Histogram';
import { NoData } from 'src/components/common/noData/NoData';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { useQueryHistogramStackInfo } from '../../../../../../../../../components/charts/helpers/useQueryHistogramStackInfo';
import { InsightsHistogramTooltip } from '../../../../../../../insights/InsightsHistogramTooltip';
import { HistogramTrace } from '../histogramTraceMapper/HistogramTraceMapper';

type Props = {
  loading: boolean;
  histogramTraceProjection: ReadonlyArray<HistogramTrace> | null;
  activeBucket: number | null;
  onHistogramClick: (index: number) => void;
  onTraceBucketSelect: (trace: number | undefined | null) => void;
  histogramData: ReturnType<typeof useQueryHistogramStackInfo> & {
    data: ReturnType<typeof usePrepareHistogramData>;
  };
  nextSlowerTrace: HistogramTrace | null;
  nextFasterTrace: HistogramTrace | null;
  durationBuckets: number[];
};

export const HistogramSection = ({
  loading,
  activeBucket,
  onHistogramClick,
  onTraceBucketSelect,
  histogramData,
  nextSlowerTrace,
  nextFasterTrace,
}: Props) => {
  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          Request latency distribution
          <Tooltip
            label={`Studio saves one "Sample Trace" per latency bucket for you to browse. Use the left and right arrows or click on the latency chart directly to browse sample traces in different latency buckets.`}
          >
            <IconInfo className="text-secondary" />
          </Tooltip>
        </CardTitle>
        <ButtonGroup>
          <Tooltip
            label={
              nextFasterTrace
                ? 'View a sample trace from the next lower latency bucket'
                : 'There are no sample traces to view with less latency than this one'
            }
          >
            <Button
              isDisabled={!nextFasterTrace}
              size="sm"
              variant="secondary"
              onClick={() =>
                onTraceBucketSelect(nextFasterTrace?.durationBucket)
              }
              leftIcon={<IconChevronLeft />}
            />
          </Tooltip>

          <Tooltip
            label={
              nextSlowerTrace
                ? 'View a sample trace from the next higher latency bucket'
                : 'There are no sample traces to view with more latency than this one'
            }
          >
            <Button
              className="ml-1"
              isDisabled={!nextSlowerTrace}
              size="sm"
              variant="secondary"
              onClick={() =>
                onTraceBucketSelect(nextSlowerTrace?.durationBucket)
              }
              leftIcon={<IconChevronRight />}
            />
          </Tooltip>
        </ButtonGroup>
        <CardDescription>
          The frequency of requests for this operation according to latency.
        </CardDescription>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex h-40 w-full justify-center">
            <Spinner size="md" />
          </div>
        ) : !isMinLengthArray(1, histogramData.data.data) ? (
          <NoData
            className="flex h-40 w-full justify-center"
            message="No latency data"
            secondary="Try adjusting your selected time range."
          />
        ) : (
          <CursorProvider>
            <Histogram
              marker={activeBucket ?? undefined}
              onClick={onHistogramClick}
              className="h-40 pr-4"
              onTraceSelect={onTraceBucketSelect}
              tooltipLabelAs={
                <InsightsHistogramTooltip title="Latency distribution" />
              }
              hideCursorArrow
              {...histogramData}
            />
            <SeriesControls
              className="w-full px-4 pt-6 text-center"
              hideWhenSingleStack
              {...histogramData}
            />
          </CursorProvider>
        )}
      </CardBody>
    </Card>
  );
};
